import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../components/Theme/ThemeModeContext';
import {
    Box,
    Typography
} from '@mui/material';
import DashboardCard from '../components/DashboardCard';
import {
    GoogleMap,
    Marker,
    InfoWindow
} from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { api } from 'common';
import { colors } from 'components/Theme/WebTheme';
import { FONT_FAMILY } from 'common/sharedFunctions';

const AdminDashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);
    const { fetchUserWalletHistory } = api;

    const [settings, setSettings] = useState({});
    // Theme context (like Dashboard.js)
    const themeModeContext = useThemeMode();
    const themeMode = themeModeContext && themeModeContext.themeMode ? themeModeContext.themeMode : 'system';
    // Card color palette (copied from Dashboard.js)
    const cardColors = {
        light: {
            bg: '#f8fafc',
            title: '#1e293b',
            value: '#0f172a',
        },
        dark: {
            bg: '#23263A',
            title: '#e0e7ef',
            value: '#fff',
        },
        system: {
            bg: '#fff',
            title: '#23263A',
            value: '#23263A',
        }
    };
    const cardTheme = cardColors[themeMode] || cardColors.light;

    // Google Maps dark mode style (copied from Dashboard.js)
    const darkMapStyles = [
      { elementType: 'geometry', stylers: [{ color: '#212121' }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
      { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
      { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#181818' }] },
      { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#181818' }] },
      { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { featureType: 'poi.park', elementType: 'labels.text.stroke', stylers: [{ color: '#1b1b1b' }] },
      { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
      { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
      { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
      { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] },
      { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
      { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
      { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
      { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] },
    ];

    // Chart theme (copied from Dashboard.js)
    const chartTheme = themeMode === 'dark' ? {
        background: '#23263A',
        foreColor: '#e0e7ef',
        grid: { borderColor: '#374151' },
        xaxis: { labels: { style: { colors: '#e0e7ef' } } },
        yaxis: { labels: { style: { colors: '#e0e7ef' } } },
        legend: { labels: { colors: '#e0e7ef' } },
        tooltip: { theme: 'dark' },
    } : {
        background: '#fff',
        foreColor: '#1e293b',
        grid: { borderColor: '#e5e7eb' },
        xaxis: { labels: { style: { colors: '#1e293b' } } },
        yaxis: { labels: { style: { colors: '#1e293b' } } },
        legend: { labels: { colors: '#1e293b' } },
        tooltip: { theme: 'light' },
    };

    useEffect(() => {
        if (themeMode === 'dark') {
            document.body.style.background = '#181A24';
        } else {
            document.body.style.background = '#f3f4f6';
        }
        return () => {
            document.body.style.background = '';
        };
    }, [themeMode]);
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const walletHistory = useSelector(state => state.auth.walletHistory);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const [monthlyEarnings, setMonthlyEarnings] = useState(0);
    const [downline, setDownline] = useState([]);
    const [commissionRates, setCommisionRates] = useState({});

    const dispatch = useDispatch();
    const {fetchDrivers, clearFetchDrivers} = api;

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        return () => {
            dispatch(clearFetchDrivers());
        };
    }, [dispatch, fetchDrivers, clearFetchDrivers]);

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }),
                error => {
                    console.log(error)
                    // Fallback to a default location if geolocation fails
                    setMylocation({
                        lat: 37.7749, // Default latitude
                        lng: -122.4194 // Default longitude
                    });
                }
            );
        }
    }, [mylocation]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if(settingsdata) {
            setCommisionRates({
                rider: settingsdata.settings.riderFixShare || 50,
                admin_munincipality: settingsdata.settings.adminMunincipality || 15,
                admin_provincial: settingsdata.settings.adminProvincial || 5,
                admin_regional: settingsdata.settings.adminRegional || 3,
                admin_national: settingsdata.settings.adminNational || 2
            });
        }
    }, [settingsdata]);

    useEffect(() => {
        if (usersdata.drivers && bookinglistdata.bookings) {
            const liveBookings = bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED');
            const drivers = usersdata.drivers;
            let locs = [];
           
                for (let i = 0; i < drivers.length; i++) {
                    if (drivers[i].location) {
                        let carImage = 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';
                        let bookingRef = null;
                        for (let j = 0; j < cars.length; j++) {
                            if (cars[j].name === drivers[i].carType) {
                                carImage = cars[j].image;
                            }
                        }
                        for(let j = 0; j<liveBookings.length; j++){
                            if(liveBookings[j].driver === drivers[i].uid){
                                bookingRef = liveBookings[j].reference;
                            }
                        }
                        locs.push({
                            id: i,
                            lat: drivers[i].location.lat,
                            lng: drivers[i].location.lng,
                            drivername: drivers[i].firstName + ' ' + drivers[i].lastName,
                            carnumber: drivers[i].vehicleNumber,
                            cartype: drivers[i].carType,
                            carImage: carImage,
                            bookingRef: bookingRef,
                            fleetadmin:drivers[i].fleetadmin
                        });
                    }
                }
                if (auth.profile.usertype === 'fleetadmin') {
                    locs = locs.filter(driver => driver.fleetadmin === auth.profile.uid);
                }
                setLocations(locs);
                
        }
    }, [usersdata.drivers, auth.profile.usertype, auth.profile.uid, cars, bookinglistdata.bookings, settings]);

    // Build complete downline tree (if needed)
    useEffect(() => {
        if (usersdata.users && auth.profile?.uid) {
            const buildDownline = (userId) => {
            const affiliates = usersdata.users.filter(user => user.affiliate === userId);
            return affiliates.map(affiliate => ({
                ...affiliate,
                downline: buildDownline(affiliate.id),
            }));
            };

            const userDownline = buildDownline(auth.profile.uid);
            setDownline(userDownline);
        }
    }, [usersdata.users, auth]);

    // Compute daily and monthly commission from booking data using downline riders
    useEffect(() => {
        if (bookinglistdata.bookings && downline.length > 0) {
            // Flatten the downline tree into a flat array
            const flattenDownline = (nodes) => {
            let result = [];
            nodes.forEach(node => {
                result.push(node);
                if (node.downline && node.downline.length > 0) {
                result = result.concat(flattenDownline(node.downline));
                }
            });
            return result;
            };

            const allDownline = flattenDownline(downline);
            // Assuming the riders are identified by user.usertype === 'driver' or similar
            // (Adjust the usertype here if necessary)
            const riderIds = allDownline
            .filter(user => user.usertype === 'driver')
            .map(user => user.id);

            let dailyBookingCount = 0;
            let monthlyBookingCount = 0;
            const today = new Date();

            // Loop through all bookings
            Object.values(bookinglistdata.bookings).forEach(booking => {
                if (booking.status === 'PAID' || booking.status === 'COMPLETE') {
                    // Check if booking belongs to a rider from the downline
                    if (riderIds.includes(booking.driver)) {
                    const bookingDate = new Date(booking.bookingDate);
                    // Compare full date for daily count
                    if (bookingDate.toDateString() === today.toDateString()) {
                        dailyBookingCount++;
                    }
                    // Compare month and year for monthly count
                    if (
                        bookingDate.getMonth() === today.getMonth() &&
                        bookingDate.getFullYear() === today.getFullYear()
                    ) {
                        monthlyBookingCount++;
                    }
                    }
                }
            });

            // Calculate commission per level based on counts
            const daily = {
            admin_national: dailyBookingCount * commissionRates.admin_national,
            admin_regional: dailyBookingCount * commissionRates.admin_regional,
            admin_provincial: dailyBookingCount * commissionRates.admin_provincial,
            admin_munincipality: dailyBookingCount * commissionRates.admin_munincipality,
            rider: dailyBookingCount * commissionRates.rider,
            };

            const monthly = {
            admin_national: monthlyBookingCount * commissionRates.admin_national,
            admin_regional: monthlyBookingCount * commissionRates.admin_regional,
            admin_provincial: monthlyBookingCount * commissionRates.admin_provincial,
            admin_munincipality: monthlyBookingCount * commissionRates.admin_munincipality,
            rider: monthlyBookingCount * commissionRates.rider,
            };

            setDailygross(parseFloat(daily[auth.profile.usertype] || 0).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthly[auth.profile.usertype] || 0).toFixed(settings.decimal));

        }
    }, [auth.profile.usertype, bookinglistdata.bookings, downline, settings.decimal, commissionRates]);
      
      // Total Gross - Direct wallet balance
      useEffect(() => {
        if (auth.profile?.walletBalance !== undefined) {
          setTotalgross(parseFloat(auth.profile.walletBalance).toFixed(settings.decimal));
        }
      }, [auth.profile, settings.decimal]);
      

      const barChartOptions = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        colors: [colors.GREEN, colors.RED],
        options: {
            maintainAspectRatio: false,
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                        },
                    },
                ],
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'smooth'
            },
            tooltip: {
                enabled: true, // Ensure tooltip is enabled
                shared: true,
                intersect: false,
            },
            legend: {
                show: true, // Ensure legend is shown
                fontFamily: FONT_FAMILY
            }
        },
        chart: {
            height: 350,
            type: 'area',
            fontFamily: FONT_FAMILY
        },
        xaxis: {
            labels: {
                style: {
                    fontFamily: FONT_FAMILY
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    fontFamily: FONT_FAMILY
                }
            }
        },
        title: {
            style: {
                fontFamily: FONT_FAMILY
            }
        },
        subtitle: {
            style: {
                fontFamily: FONT_FAMILY
            }
        }
    };

    const barChart = [{
        name: t('completed_bookings'),
        data: Array.isArray(allCompleteCount) ? allCompleteCount : []
    }, {
        name: t('cancelled_bookings'),
        data: Array.isArray(allCancelCount) ? allCancelCount : []
    }];

    const barChartEarnings = [{
        name: t('monthly_earnings'),
        data: Array.isArray(monthlyEarnings) ? monthlyEarnings : []
    }];

    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === 'driver') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin_munincipality') {
                        driverCount = driverCount + 1;
                    }
                }
                if (usersdata.users[i].driverActiveStatus === true) {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin_munincipality') {
                        activeCount = activeCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === 'customer') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin_munincipality') {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0)
            setDriverCount(0)
            setCustomerCount(0)
        }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        dispatch(fetchUserWalletHistory(auth.profile.uid));
    }, [auth.profile.uid, dispatch, fetchUserWalletHistory]);

    useEffect(() => {
        let allCompleteCount = new Array(12).fill(0);
        let allCancelCount = new Array(12).fill(0);
        let allMonthlyEarnings = new Array(12).fill(0);

        if(walletHistory && walletHistory.length > 0) {
            walletHistory.forEach(history => {
                const booking = bookinglistdata.bookings.find(bkg => bkg.id === history.transaction_id);
                if (booking) {
                    const { tripdate, status } = booking;
                    const tDate = new Date(tripdate);
                    const monthIndex = tDate.getMonth();
                    if (status === 'PAID' || status === 'COMPLETE') {
                        allCompleteCount[monthIndex] += 1;
                        allMonthlyEarnings[monthIndex] += history.amount;
                    }
                    if (status === 'CANCELLED') {
                        allCancelCount[monthIndex] += 1;
                    }
                }
            });
        }
    
        setAllCompleteCount(allCompleteCount);
        setAllCancelCount(allCancelCount);
        setMonthlyEarnings(allMonthlyEarnings);
    }, [auth.profile.usertype, bookinglistdata.bookings, commissionRates, walletHistory]);

    return (
        bookinglistdata.loading || usersdata.loading ? <CircularLoading /> :
            <div style={{ minHeight: '100vh', background: themeMode === 'dark' ? '#181A24' : '#f3f4f6', transition: 'background 0.3s' }}>
                <div style={{ background: themeMode === 'dark' ? '#181A24' : '#f3f4f6', padding: 0, margin: 0 }}>
                    <Typography variant="h4" style={{ margin: "20px 20px 0 15px", textAlign: isRTL === 'rtl' ? 'right' : 'left', fontFamily: FONT_FAMILY, color: themeMode === 'dark' ? '#e0e7ef' : '#1e293b' }}>{t('gross_earning')}</Typography>
                </div>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        <DashboardCard
                            themeMode={themeMode}
                            crdStyle={{
                                display: 'flex',
                                borderRadius: '19px',
                                background: themeMode === 'system' ? colors.WALLET_CARD : cardTheme.bg,
                                color: themeMode === 'system' ? '#fff' : cardTheme.value,
                                boxShadow: themeMode === 'dark' ? '0 2px 16px 0 rgba(0,0,0,0.65)' : '0 2px 16px 0 rgba(11,31,58,0.06)',
                            }}
                            title={t('today_text')}
                            image={require('../assets/img/money1.jpg')}
                        >
                            <span style={{ color: themeMode === 'system' ? '#fff' : cardTheme.value }}>{settings.swipe_symbol === false ? settings.symbol + ' ' + dailygross : dailygross + ' ' + settings.symbol}</span>
                        </DashboardCard>
                    </Grid>
                    <Grid item xs style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        <DashboardCard
                            themeMode={themeMode}
                            crdStyle={{
                                display: 'flex',
                                borderRadius: '19px',
                                background: themeMode === 'system' ? colors.VIOLET : cardTheme.bg,
                                color: themeMode === 'system' ? '#fff' : cardTheme.value,
                                boxShadow: themeMode === 'dark' ? '0 2px 16px 0 rgba(0,0,0,0.65)' : '0 2px 16px 0 rgba(11,31,58,0.06)',
                            }}
                            title={t('this_month_text')}
                            image={require('../assets/img/money2.jpg')}
                        >
                            <span style={{ color: themeMode === 'system' ? '#fff' : cardTheme.value }}>{settings.swipe_symbol === false ? settings.symbol + ' ' + monthlygross : monthlygross + ' ' + settings.symbol}</span>
                        </DashboardCard>
                    </Grid>
                    <Grid item xs style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        <DashboardCard
                            themeMode={themeMode}
                            crdStyle={{
                                display: 'flex',
                                borderRadius: '19px',
                                background: themeMode === 'system' ? colors.GREEN : cardTheme.bg,
                                color: themeMode === 'system' ? '#fff' : cardTheme.value,
                                boxShadow: themeMode === 'dark' ? '0 2px 16px 0 rgba(0,0,0,0.65)' : '0 2px 16px 0 rgba(11,31,58,0.06)',
                            }}
                            title={t('total')}
                            image={require('../assets/img/money3.jpg')}
                        >
                            <span style={{ color: themeMode === 'system' ? '#fff' : cardTheme.value }}>{settings.swipe_symbol === false ? settings.symbol + ' ' + totalgross : totalgross + ' ' + settings.symbol}</span>
                        </DashboardCard>
                    </Grid>
                </Grid>
                {auth.profile.usertype === "admin_munincipality" ?
                    <Grid item xs={12} style={{ marginTop: 20 }}>
                        <Grid container style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
                            <Grid item xs={12} sm={12} md={9} lg={9}
                                style={{ background: themeMode === 'dark' ? '#23263A' : '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: 3, transition: 'background 0.3s' }}>
                                <Typography variant="h4" style={{ margin: '20px 20px 0 15px', textAlign: isRTL === 'rtl' ? 'right' : 'left', fontFamily: FONT_FAMILY, color: themeMode === 'dark' ? '#e0e7ef' : '#1e293b' }}>{t('real_time_driver_section_text')}</Typography>
                                {mylocation && mylocation.lat ?
                                    <GoogleMap
                                        zoom={10}
                                        center={mylocation}
                                        mapContainerStyle={{ height: '380px', fontFamily: FONT_FAMILY }}
                                        options={themeMode === 'dark' ? { styles: darkMapStyles } : { styles: undefined }}>
                                        {locations.map(marker => (
                                            <Marker
                                                position={{ lat: marker.lat, lng: marker.lng }}
                                                key={marker.id}
                                                icon={{
                                                    url: marker.carImage,
                                                    scaledSize: new window.google.maps.Size(35, 25)
                                                }}
                                            >
                                                <InfoWindow
                                                    position={{ lat: marker.lat, lng: marker.lng }}
                                                    options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                                >
                                                    <div style={{ fontFamily: FONT_FAMILY }}>{marker.drivername}<br />{marker.carnumber}</div>
                                                </InfoWindow>
                                            </Marker>
                                        ))}
                                    </GoogleMap>
                                    : null}
                            </Grid>
                            <Grid item xs={12} sm={12} md={3} lg={3} style={{ padding: 25, marginTop: '30px', background: 'none', boxShadow: 'none' }}>
                                {/* Total Customer Card */}
                                <Card style={{
                                    borderRadius: '19px',
                                    background: themeMode === 'system' ? colors.WALLET_CARD : cardTheme.bg,
                                    minHeight: 100,
                                    marginBottom: 20
                                }}>
                                    <Typography style={{ color: themeMode === 'system' ? '#fff' : cardTheme.value, textAlign: 'center', fontSize: 20, marginTop: 20, fontFamily: FONT_FAMILY }}>{t('total_cumtomer')}</Typography>
                                    <Typography style={{ color: themeMode === 'system' ? '#fff' : cardTheme.value, textAlign: 'center', fontSize: 26, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{customerCount}</Typography>
                                </Card>
                                {/* Total Drivers Card */}
                                <Card style={{
                                    borderRadius: '19px',
                                    background: themeMode === 'system' ? colors.VIOLET : cardTheme.bg,
                                    minHeight: 100,
                                    marginBottom: 20
                                }}>
                                    <Typography style={{ color: themeMode === 'system' ? '#fff' : cardTheme.value, textAlign: 'center', fontSize: 20, marginTop: 20, fontFamily: FONT_FAMILY }}>{t('total_drivers')}</Typography>
                                    <Typography style={{ color: themeMode === 'system' ? '#fff' : cardTheme.value, textAlign: 'center', fontSize: 26, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{driverCount}</Typography>
                                </Card>
                                {/* Active Drivers Card */}
                                <Card style={{
                                    borderRadius: '19px',
                                    background: themeMode === 'system' ? colors.GREEN : cardTheme.bg,
                                    minHeight: 100
                                }}>
                                    <Typography style={{ color: themeMode === 'system' ? '#fff' : cardTheme.value, textAlign: 'center', fontSize: 20, marginTop: 20, fontFamily: FONT_FAMILY }}>{t('active_driver')}</Typography>
                                    <Typography style={{ color: themeMode === 'system' ? '#fff' : cardTheme.value, textAlign: 'center', fontSize: 26, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{activeCount}</Typography>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                    : null}
                <Box style={{ padding: 10, marginTop: 20, borderRadius: 20, background: themeMode === 'dark' ? '#23263A' : '#fff', transition: 'background 0.3s' }} boxShadow={6}>
                    <Typography style={{ margin: '5px 20px 0 15px', color: themeMode === 'dark' ? '#22d3ee' : colors.GREEN, fontWeight: 'bold', fontFamily: FONT_FAMILY }} variant="h5">{auth.profile.usertype === "admin_munincipality" ? t('booking_chart') : t('earning_chart')}</Typography>
                    <Chart
                        options={{ ...barChartOptions, theme: chartTheme }}
                        series={auth.profile.usertype === "admin_munincipality" ? barChart : barChartEarnings}
                        type="area"
                        width="100%"
                        height={360}
                    />
                </Box>
            </div>
    )
}

export default AdminDashboard;